import { useState, useEffect, useCallback } from 'react';
import fontLoader from '@dashboard/utils/fontLoader';

/**
 * React hook for managing font loading state
 * @param {string} fontName - Name of the font to load
 * @param {boolean} shouldLoad - Whether to load the font (default: true)
 * @returns {object} - { isLoaded, isLoading, isFailed, loadFont }
 */
export const useFontLoader = (fontName, shouldLoad = true) => {
  const [isLoaded, setIsLoaded] = useState(fontLoader.isFontLoaded(fontName));
  const [isLoading, setIsLoading] = useState(fontLoader.isFontLoading(fontName));
  const [isFailed, setIsFailed] = useState(fontLoader.isFontFailed(fontName));

  const loadFont = useCallback(async () => {
    if (!fontName || fontName === 'default') {
      return true;
    }

    setIsLoading(true);
    setIsFailed(false);

    try {
      const success = await fontLoader.loadFont(fontName);
      setIsLoaded(success);
      setIsFailed(!success);
      setIsLoading(false);
      return success;
    } catch (error) {
      setIsFailed(true);
      setIsLoading(false);
      return false;
    }
  }, [fontName]);

  useEffect(() => {
    if (!fontName || fontName === 'default') {
      setIsLoaded(true);
      setIsLoading(false);
      setIsFailed(false);
      return;
    }

    // Update state based on current font loader state
    setIsLoaded(fontLoader.isFontLoaded(fontName));
    setIsLoading(fontLoader.isFontLoading(fontName));
    setIsFailed(fontLoader.isFontFailed(fontName));

    // Load font if requested and not already handled
    if (shouldLoad && !fontLoader.isFontLoaded(fontName) && !fontLoader.isFontLoading(fontName)) {
      loadFont();
    }
  }, [fontName, shouldLoad, loadFont]);

  return {
    isLoaded,
    isLoading,
    isFailed,
    loadFont,
  };
};

/**
 * Hook for managing multiple fonts
 * @param {string[]} fontNames - Array of font names to manage
 * @param {boolean} shouldLoad - Whether to load fonts automatically
 * @returns {object} - { fontStates, loadAllFonts }
 */
export const useMultipleFonts = (fontNames, shouldLoad = true) => {
  const [fontStates, setFontStates] = useState({});

  const updateFontState = useCallback((fontName) => {
    setFontStates(prev => ({
      ...prev,
      [fontName]: {
        isLoaded: fontLoader.isFontLoaded(fontName),
        isLoading: fontLoader.isFontLoading(fontName),
        isFailed: fontLoader.isFontFailed(fontName),
      }
    }));
  }, []);

  const loadAllFonts = useCallback(async () => {
    const validFonts = fontNames.filter(name => name && name !== 'default');
    
    // Initialize states
    validFonts.forEach(fontName => {
      updateFontState(fontName);
    });

    // Load fonts
    const results = await fontLoader.loadFonts(validFonts);
    
    // Update states after loading
    validFonts.forEach(fontName => {
      updateFontState(fontName);
    });

    return results;
  }, [fontNames, updateFontState]);

  useEffect(() => {
    // Initialize font states
    fontNames.forEach(fontName => {
      if (!fontName || fontName === 'default') {
        setFontStates(prev => ({
          ...prev,
          [fontName]: { isLoaded: true, isLoading: false, isFailed: false }
        }));
      } else {
        updateFontState(fontName);
      }
    });

    if (shouldLoad) {
      loadAllFonts();
    }
  }, [fontNames, shouldLoad, loadAllFonts, updateFontState]);

  return {
    fontStates,
    loadAllFonts,
  };
};

/**
 * Hook for preloading fonts based on search query or visibility
 * @param {string[]} fontNames - Array of font names to potentially preload
 * @param {number} preloadCount - Number of fonts to preload (default: 10)
 */
export const useFontPreloader = (fontNames, preloadCount = 10) => {
  useEffect(() => {
    if (fontNames && fontNames.length > 0) {
      const fontsToPreload = fontNames
        .filter(name => name && name !== 'default')
        .slice(0, preloadCount);
      
      fontLoader.preloadFonts(fontsToPreload);
    }
  }, [fontNames, preloadCount]);
};

export default useFontLoader;